import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowUpRight, Folder, Users, Mail, X, Crown, Eye, ShieldCheck, UserCircle2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Header from "../components/Header";
import { DashboardSkeleton } from "../components/Skeletons";

const COVERS = [
  { id: "blue", bg: "#002FA7", text: "#fff" },
  { id: "red", bg: "#FF2A2A", text: "#fff" },
  { id: "yellow", bg: "#FFD700", text: "#000" },
  { id: "pink", bg: "#FF6BEB", text: "#000" },
  { id: "black", bg: "#000000", text: "#fff" },
  { id: "grid", bg: "#FFFFFF", text: "#000" },
];

function coverStyle(cover) {
  const c = COVERS.find((x) => x.id === cover) || COVERS[0];
  if (c.id === "grid") {
    return {
      background:
        "repeating-linear-gradient(0deg, #000 0 1px, transparent 1px 20px), repeating-linear-gradient(90deg, #000 0 1px, transparent 1px 20px), #ffffff",
      color: "#000",
    };
  }
  return { background: c.bg, color: c.text };
}

const ROLE_ICONS = {
  master: <Crown size={12} strokeWidth={2.5} />,
  spv: <ShieldCheck size={12} strokeWidth={2.5} />,
  member: <UserCircle2 size={12} strokeWidth={2.5} />,
  viewer: <Eye size={12} strokeWidth={2.5} />,
};

export default function Dashboard() {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [ws, bs] = await Promise.all([api.get("/workspaces"), api.get("/boards")]);
      setWorkspaces(ws.data);
      setBoards(bs.data);
      if (ws.data.length > 0 && !activeWs) setActiveWs(ws.data[0].workspace_id);
    } catch (e) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filteredBoards = activeWs ? boards.filter((b) => b.workspace_id === activeWs) : boards;
  const activeWsObj = workspaces.find((w) => w.workspace_id === activeWs);

  const deleteBoard = async (id) => {
    if (!window.confirm("Hapus papan ini secara permanen?")) return;
    try {
      await api.delete(`/boards/${id}`);
      setBoards((b) => b.filter((x) => x.board_id !== id));
      toast.success("Papan dihapus");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header
        breadcrumb={
          <>
            <span>RUANG KERJA</span>
            <span className="text-black/40">/</span>
            <span className="text-black truncate max-w-[180px]">{activeWsObj?.name || "—"}</span>
          </>
        }
      />

      <div className="flex max-w-[1600px] mx-auto">
        {/* Workspace sidebar */}
        <aside className="hidden md:flex flex-col w-60 border-r-2 border-black sticky top-20 self-start h-[calc(100vh-5rem)]" data-testid="ws-sidebar">
          <div className="p-4 border-b border-black bg-[#F0F0F0]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-1">RUANG KERJA</div>
            <div className="font-heading font-black text-lg uppercase tracking-tight">Workspace</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {workspaces.map((w) => {
              const active = w.workspace_id === activeWs;
              return (
                <button
                  key={w.workspace_id}
                  onClick={() => setActiveWs(w.workspace_id)}
                  data-testid={`ws-${w.workspace_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-black/10 flex items-center justify-between btn-brut ${
                    active ? "bg-black text-white" : "bg-white hover:bg-canary"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{w.name}</div>
                    <div className={`font-mono text-[9px] uppercase tracking-widest ${active ? "text-white/60" : "text-black/50"} flex items-center gap-1 mt-0.5`}>
                      {ROLE_ICONS[w.my_role]} {w.my_role}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="p-3 border-t border-black space-y-2">
            <button
              onClick={async () => {
                const name = window.prompt("Nama ruang kerja baru?");
                if (!name?.trim()) return;
                const r = await api.post("/workspaces", { name: name.trim() });
                setWorkspaces((ws) => [...ws, r.data]);
                setActiveWs(r.data.workspace_id);
                toast.success("Workspace dibuat");
              }}
              data-testid="new-workspace-btn"
              className="w-full bg-white border-2 border-black px-3 py-2 font-mono uppercase text-[10px] tracking-widest btn-brut hover:bg-klein hover:text-white hover:-translate-y-0.5 hover:shadow-brut flex items-center justify-center gap-2"
            >
              <Plus size={12} strokeWidth={3} /> Workspace Baru
            </button>
            {activeWsObj && activeWsObj.my_role === "master" && (
              <button
                onClick={() => setShowMembers(true)}
                data-testid="manage-members-btn"
                className="w-full bg-canary border-2 border-black px-3 py-2 font-mono uppercase text-[10px] tracking-widest btn-brut hover:-translate-y-0.5 hover:shadow-brut flex items-center justify-center gap-2"
              >
                <Users size={12} strokeWidth={3} /> Kelola Anggota
              </button>
            )}
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-4 md:px-10 py-6 md:py-10 pb-24 md:pb-10" data-testid="dashboard-main">
          {/* Mobile workspace switcher */}
          <div className="md:hidden mb-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-2">RUANG KERJA</div>
            <div className="relative">
              <select
                value={activeWs || ""}
                onChange={(e) => setActiveWs(e.target.value)}
                data-testid="mobile-ws-select"
                className="w-full appearance-none bg-white border-2 border-black px-4 py-3 pr-10 font-bold text-sm uppercase tracking-tight btn-brut focus:shadow-brutBlue"
              >
                {workspaces.map((w) => (
                  <option key={w.workspace_id} value={w.workspace_id}>
                    {w.name} ({w.my_role})
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            {activeWsObj && activeWsObj.my_role === "master" && (
              <button
                onClick={() => setShowMembers(true)}
                data-testid="mobile-manage-members-btn"
                className="mt-2 w-full bg-canary border-2 border-black px-3 py-2 font-mono uppercase text-[10px] tracking-widest btn-brut hover:-translate-y-0.5 hover:shadow-brut flex items-center justify-center gap-2"
              >
                <Users size={12} strokeWidth={3} /> Kelola Anggota
              </button>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-black/60 mb-2">
                [ 02 / DASBOR ]
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight md:tracking-tighter leading-[0.95] md:leading-[0.9]">
                Papan
                <br />
                <span className="text-klein">Kerja Anda.</span>
              </h1>
              <p className="font-mono text-sm text-black/60 mt-3 max-w-xl">
                {filteredBoards.length === 0
                  ? "Belum ada papan. Buat papan pertama Anda untuk mulai mengatur kerja."
                  : `${filteredBoards.length} papan aktif di workspace ini.`}
              </p>
            </div>
            {activeWsObj && (
              <button
                onClick={() => setShowCreate(true)}
                data-testid="open-create-board-btn"
                className="w-full sm:w-auto self-start md:self-end inline-flex items-center justify-center gap-2 bg-klein text-white border-2 border-black px-6 py-3 font-mono uppercase text-sm tracking-wider btn-brut hover:bg-black hover:-translate-y-1 hover:shadow-brut"
              >
                <Plus size={16} strokeWidth={3} /> Papan Baru
              </button>
            )}
          </div>

          {filteredBoards.length === 0 ? (
            <EmptyBoards onCreate={() => setShowCreate(true)} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="boards-grid">
              {filteredBoards.map((b, i) => (
                <BoardCard key={b.board_id} board={b} idx={i}
                  onOpen={() => navigate(`/board/${b.board_id}`)}
                  onDelete={() => deleteBoard(b.board_id)} />
              ))}
            </div>
          )}
        </main>
      </div>

      {showCreate && activeWsObj && (
        <CreateBoardModal
          workspaceId={activeWs}
          onClose={() => setShowCreate(false)}
          onCreated={(b) => {
            setBoards((bs) => [b, ...bs]);
            setShowCreate(false);
            toast.success("Papan dibuat");
            navigate(`/board/${b.board_id}`);
          }}
        />
      )}

      {showMembers && activeWsObj && (
        <WorkspaceMembersModal
          workspaceId={activeWs}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}

function EmptyBoards({ onCreate }) {
  return (
    <div className="border-2 border-black p-5 md:p-12 bg-grid relative" data-testid="empty-boards-state">
      <div className="absolute top-0 left-0 bg-black text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1">
        TIDAK ADA PAPAN
      </div>
      <div className="flex flex-col items-start gap-6 max-w-md pt-6">
        <Folder size={48} strokeWidth={1.5} />
        <h3 className="font-heading font-black text-3xl uppercase tracking-tight">Mulai dari nol.</h3>
        <p className="font-mono text-sm text-black/70">
          Buat papan baru. Tiga daftar default (TO DO, DIKERJAKAN, SELESAI) + 4 label otomatis dibuat.
        </p>
        <button onClick={onCreate} data-testid="empty-create-board-btn"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-black text-white border-2 border-black px-6 py-3 font-mono uppercase text-sm tracking-wider btn-brut hover:bg-klein hover:-translate-y-1 hover:shadow-brut">
          <Plus size={16} strokeWidth={3} /> Buat Papan Pertama
        </button>
      </div>
    </div>
  );
}

function BoardCard({ board, onOpen, onDelete, idx }) {
  const style = coverStyle(board.cover);
  return (
    <div data-testid={`board-card-${board.board_id}`}
      className="group relative border-2 border-black bg-white btn-brut hover:-translate-y-1 hover:translate-x-1 hover:shadow-brutLg animate-blockIn"
      >
      <button onClick={onOpen} className="w-full text-left" data-testid={`open-board-${board.board_id}`}>
        <div className="h-32 border-b-2 border-black relative overflow-hidden" style={style}>
          <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest opacity-70">
            #{String(idx + 1).padStart(3, "0")}
          </div>
          <div className="absolute bottom-3 right-3 opacity-80 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform">
            <ArrowUpRight size={28} strokeWidth={2.5} />
          </div>
        </div>
        <div className="p-5 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">PAPAN</div>
          <h3 className="font-heading font-black text-2xl uppercase tracking-tight leading-tight line-clamp-2">
            {board.title}
          </h3>
          {board.description && (
            <p className="font-mono text-xs text-black/60 line-clamp-2">{board.description}</p>
          )}
        </div>
      </button>
      <button onClick={onDelete} data-testid={`delete-board-${board.board_id}`} title="Hapus"
        className="absolute top-3 right-3 w-10 h-10 md:w-9 md:h-9 bg-white border border-black opacity-100 md:opacity-0 md:group-hover:opacity-100 flex items-center justify-center hover:bg-signal hover:text-white transition-all">
        <Trash2 size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function CreateBoardModal({ workspaceId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState("blue");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/boards", {
        title: title.trim(), description, cover, workspace_id: workspaceId,
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat papan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-2 md:p-4 animate-fadeIn" data-testid="create-board-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-xl bg-white border-2 border-black shadow-brutLg animate-blockIn max-h-[94dvh] overflow-y-auto">
        <div className="border-b-2 border-black p-4 md:p-6 flex items-center justify-between gap-3 bg-canary sticky top-0 z-10">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest">[ FORMULIR / 01 ]</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight">Papan Baru</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="close-create-board"
            className="w-10 h-10 shrink-0 border border-black bg-white hover:bg-black hover:text-white transition-colors">✕</button>
        </div>
        <div className="p-4 md:p-6 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest block mb-2">Judul *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus data-testid="board-title-input"
              placeholder="mis. Rilis Q1"
              className="w-full bg-white border-2 border-black px-4 py-3 font-mono text-sm focus:shadow-brutBlue transition-all" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest block mb-2">Deskripsi</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="board-desc-input"
              rows={3} placeholder="Singkat dan tegas."
              className="w-full bg-white border-2 border-black px-4 py-3 font-mono text-sm focus:shadow-brutBlue transition-all resize-none" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest block mb-2">Sampul</label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {COVERS.map((c) => (
                <button key={c.id} type="button" onClick={() => setCover(c.id)} data-testid={`cover-${c.id}`}
                  className={`h-12 border-2 transition-all ${cover === c.id ? "border-klein shadow-brutBlue -translate-x-0.5 -translate-y-0.5" : "border-black hover:-translate-y-0.5 hover:shadow-brut"}`}
                  style={coverStyle(c.id)} />
              ))}
            </div>
          </div>
        </div>
        <div className="border-t-2 border-black p-4 md:p-6 grid grid-cols-2 md:flex md:justify-end gap-3">
          <button type="button" onClick={onClose} className="bg-white border-2 border-black px-5 py-2.5 font-mono uppercase text-xs tracking-wider hover:bg-black hover:text-white transition-colors">Batal</button>
          <button type="submit" disabled={submitting || !title.trim()} data-testid="submit-create-board"
            className="bg-klein text-white border-2 border-black px-6 py-2.5 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut disabled:opacity-40">
            {submitting ? "Membuat…" : "Buat →"}
          </button>
        </div>
      </form>
    </div>
  );
}

function WorkspaceMembersModal({ workspaceId, onClose }) {
  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${workspaceId}/members`);
      setData(r.data);
    } catch { /* */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/workspaces/${workspaceId}/invite`, { email: email.trim() });
      if (r.data.status === "added") toast.success("Pengguna ditambahkan");
      else if (r.data.status === "pending") toast.success("Undangan dikirim (akan aktif saat login)");
      else toast.info("Sudah anggota");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal undang");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("Keluarkan anggota?")) return;
    try {
      await api.delete(`/workspaces/${workspaceId}/members/${uid}`);
      load();
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-2 md:p-4 animate-fadeIn" data-testid="ws-members-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white border-2 border-black shadow-brutLg animate-blockIn max-h-[94dvh] md:max-h-[90vh] flex flex-col">
        <div className="border-b-2 border-black p-4 md:p-6 bg-black text-white flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">[ WORKSPACE ]</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight flex items-center gap-2">
              <Users size={20} strokeWidth={2.5} /> Anggota
            </h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 shrink-0 border border-white hover:bg-white hover:text-black transition-colors"><X size={16} className="mx-auto" strokeWidth={2.5} /></button>
        </div>
        <div className="p-4 md:p-6 overflow-y-auto flex-1 space-y-6">
          <form onSubmit={invite} className="grid grid-cols-1 sm:flex gap-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ws-invite-email"
              placeholder="email@contoh.com"
              className="w-full sm:flex-1 bg-white border-2 border-black px-3 py-2 font-mono text-sm focus:shadow-brutBlue transition-all" />
            <button type="submit" disabled={submitting} data-testid="ws-invite-submit"
              className="w-full sm:w-auto bg-klein text-white border-2 border-black px-5 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut">
              <Mail size={14} className="inline mr-1" strokeWidth={2.5} /> Undang
            </button>
          </form>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest mb-2">Anggota Aktif</div>
            <div className="border-2 border-black divide-y divide-black/10">
              {data.members.map((m) => (
                <div key={m.user_id} className="p-3 flex flex-wrap md:flex-nowrap items-center justify-between gap-3" data-testid={`ws-member-${m.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {m.user?.picture ? (
                      <img src={m.user.picture} alt="" className="w-9 h-9 border border-black object-cover" />
                    ) : (
                      <div className="w-9 h-9 bg-canary border border-black flex items-center justify-center font-mono text-xs font-bold">
                        {(m.user?.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{m.user?.name}</div>
                      <div className="font-mono text-[10px] text-black/50 truncate">{m.user?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-[9px] uppercase tracking-widest bg-black text-white px-2 py-1 flex items-center gap-1">
                      {ROLE_ICONS[m.role]} {m.role}
                    </span>
                    {m.role !== "master" && (
                      <button onClick={() => remove(m.user_id)} className="w-8 h-8 border border-black bg-white hover:bg-signal hover:text-white transition-colors">
                        <Trash2 size={12} className="mx-auto" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {data.pending_invites.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest mb-2">Undangan Tertunda</div>
              <div className="border-2 border-black border-dashed divide-y divide-black/10">
                {data.pending_invites.map((inv) => (
                  <div key={inv.invite_id} className="p-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-xs min-w-0 truncate">{inv.email}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest bg-canary px-2 py-1 border border-black">TERTUNDA</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
